import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { H1Title, Paragraph } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import JudgingService from "../../services/judgingService";
import SubmissionService from "../../services/submissionService";
import CompetitionService from "../../services/competitionService";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faChartBar } from "@fortawesome/free-solid-svg-icons";

export default function AdminJudges() {
  const [judges, setJudges] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [round, setRound] = useState('round1');
  const [competitionId, setCompetitionId] = useState(null);
  const [selectedJudge, setSelectedJudge] = useState('');
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [computing, setComputing] = useState(false);
  const [rankings, setRankings] = useState(null);
  const [noCompetition, setNoCompetition] = useState(false);

  useEffect(() => { loadData(); }, [round]);

  const loadData = async () => {
    setLoading(true);
    setNoCompetition(false);
    let cId;
    try {
      const compRes = await CompetitionService.getActive();
      cId = compRes.data.data.competition._id;
      setCompetitionId(cId);
    } catch (err) {
      if (err.response?.status === 404) {
        setNoCompetition(true);
      } else {
        ToastError(err.response?.data?.error?.message || 'Failed to load competition');
      }
      setLoading(false);
      return;
    }

    try {
      const [judgeRes, subRes] = await Promise.all([
        JudgingService.getJudgesOverview(round),
        SubmissionService.getAll({ competitionId: cId, currentRound: round })
      ]);
      setJudges(judgeRes.data.data.judges || []);
      const eligible = (subRes.data.data.submissions || []).filter(s => s.status !== 'draft' && s.status !== 'eliminated');
      setSubmissions(eligible);
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to load judges or submissions');
    } finally { setLoading(false); }
  };

  const handleBulkAssign = async () => {
    if (!selectedJudge || selectedSubmissions.length === 0) {
      ToastError('Select a judge and at least one submission');
      return;
    }
    setAssigning(true);
    try {
      await JudgingService.bulkAssign({ judgeId: selectedJudge, submissionIds: selectedSubmissions, round });
      Success(`Assigned ${selectedSubmissions.length} submissions`);
      setSelectedSubmissions([]);
      loadData();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Assignment failed');
    } finally { setAssigning(false); }
  };

  const toggleSubmission = (id) => {
    setSelectedSubmissions(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedSubmissions.length === submissions.length) setSelectedSubmissions([]);
    else setSelectedSubmissions(submissions.map(s => s._id));
  };

  const handleComputeScores = async () => {
    if (!competitionId) return;
    setComputing(true);
    try {
      const { data } = await JudgingService.computeCompositeScores({ competitionId });
      setRankings(data.data.rankings);
      Success('Composite scores computed');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to compute');
    } finally { setComputing(false); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;

  if (noCompetition) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <H1Title size="sm">No Active Competition</H1Title>
        <Paragraph className="text-grey-667 mt-2 mb-6">
          Judge management is tied to an active competition. Create one (or reopen registration on the current one) first.
        </Paragraph>
        <Link to="/admin/competition">
          <AppButton fullWidth={false} className="px-8">Go to Competition Settings</AppButton>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <H1Title size="sm">Judge Management</H1Title>
        <select value={round} onChange={(e) => setRound(e.target.value)} className="border border-grey-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="round1">Round 1</option>
          <option value="round2">Round 2</option>
        </select>
      </div>

      {/* Judges Overview */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold mb-4">Judges ({judges.length})</h3>
        {judges.length === 0 ? (
          <p className="text-sm text-grey-667">No judges registered yet. Create judge accounts first.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {judges.map(j => (
              <div
                key={j.judge._id}
                onClick={() => setSelectedJudge(j.judge._id)}
                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                  selectedJudge === j.judge._id ? 'border-primary bg-primary-bg' : 'border-grey-200 hover:border-grey-border'
                }`}
              >
                <p className="font-semibold text-sm">{j.judge.firstName} {j.judge.lastName}</p>
                <p className="text-xs text-grey-98">{j.judge.email}</p>
                <p className="text-xs text-grey-667 mt-1">{j.assignmentCount} assignments</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assignment Tool */}
      {selectedJudge && (
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">
              Assign Submissions ({selectedSubmissions.length} selected)
            </h3>
            <div className="flex gap-2">
              <AppButton onClick={selectAll} variant="outline" size="sm" fullWidth={false} className="px-4">
                {selectedSubmissions.length === submissions.length ? 'Deselect All' : 'Select All'}
              </AppButton>
              <AppButton onClick={handleBulkAssign} size="sm" fullWidth={false} className="px-4" loading={assigning} disabled={selectedSubmissions.length === 0}>
                <FontAwesomeIcon icon={faUserPlus} className="mr-1" /> Assign
              </AppButton>
            </div>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {submissions.map(sub => {
              const isSelected = selectedSubmissions.includes(sub._id);
              return (
                <div
                  key={sub._id}
                  onClick={() => toggleSubmission(sub._id)}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    isSelected ? 'bg-primary-bg border border-primary/30' : 'hover:bg-grey-bg border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSubmission(sub._id)}
                    className="w-4 h-4 accent-primary pointer-events-none"
                  />
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${isSelected ? 'text-primary' : ''}`}>{sub.submitterId?.firstName} {sub.submitterId?.lastName}</p>
                    <p className="text-xs text-grey-98">{sub.teamId?.name || 'Individual'}</p>
                  </div>
                  <span className="text-xs text-grey-667">{sub.currentRound}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Composite Score Tool */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Score Compilation</h3>
          <AppButton onClick={handleComputeScores} size="sm" fullWidth={false} className="px-4" loading={computing}>
            <FontAwesomeIcon icon={faChartBar} className="mr-1" /> Compute Rankings
          </AppButton>
        </div>

        {rankings && (
          <table className="w-full text-sm">
            <thead className="bg-grey-bg border-b border-grey-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">#</th>
                <th className="text-left px-3 py-2 font-semibold">Submitter</th>
                <th className="text-left px-3 py-2 font-semibold">Team</th>
                <th className="text-right px-3 py-2 font-semibold">R1</th>
                <th className="text-right px-3 py-2 font-semibold">R2</th>
                <th className="text-right px-3 py-2 font-semibold">Composite</th>
                <th className="text-center px-3 py-2 font-semibold">Threshold</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-200">
              {rankings.map((r, i) => (
                <tr key={r.submissionId} className={i === 0 ? 'bg-yellow-500/5' : ''}>
                  <td className="px-3 py-2 font-bold">{i + 1}</td>
                  <td className="px-3 py-2">{r.submitter}</td>
                  <td className="px-3 py-2 text-grey-667">{r.team}</td>
                  <td className="px-3 py-2 text-right">{r.round1Score ?? '-'}</td>
                  <td className="px-3 py-2 text-right">{r.round2Score ?? '-'}</td>
                  <td className="px-3 py-2 text-right font-bold">{r.compositeScore ?? '-'}</td>
                  <td className="px-3 py-2 text-center">
                    {r.meetsThreshold ? (
                      <span className="text-green text-xs">&#10003;</span>
                    ) : (
                      <span className="text-red-500 text-xs">&#10007;</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
